import {iterableAny} from '@wix/devzai-utils-common';
import type {DoppeDtoAction} from '../../client-server-common/types/doppe-dto-action';
import {
    doppeActionCtaButtonsFindDownloadFileMediaFileId,
} from '../../client-server-common/types/doppe-action-cta-button-utils';
import type {DoppeActionTypeDownloadFile} from './doppe-action-type-download-file';
import {doppeActionTypeDownloadFile} from './doppe-action-type-download-file';
import type {DoppeActionTypeDownloadFilePreview} from '../doppe-action-type-download-file-preview/doppe-action-type-download-file-preview';
import {doppeActionTypeDownloadFilePreview} from '../doppe-action-type-download-file-preview/doppe-action-type-download-file-preview';
import type {DoppeActionTypeDownloadFilesList} from '../doppe-action-type-download-files-list/doppe-action-type-download-files-list';
import {doppeActionTypeDownloadFilesList} from '../doppe-action-type-download-files-list/doppe-action-type-download-files-list';

/**
 * Resolves the Wix Media file id bound to a download-capable action.
 *
 * Four sources are considered, in order:
 *
 * 1. The top-level `doppeActionTypeDownloadFile` action — file id lives
 *    directly on `action.settings.fileId`.
 * 2. The `doppeActionTypeDownloadFilePreview` content-widget action — file
 *    id lives inside `action.settings.fileSettings` (all-or-nothing
 *    `{fileId, fileName}` struct that is `null` when no file is selected).
 *    The seeded `DownloadMainFile` CTA reads from this struct at click time
 *    rather than carrying its own copy.
 * 3. The `doppeActionTypeDownloadFilesList` list-action — file ids live on
 *    each `listDataItems[*].fileSettings`. **Best-effort:** returns the
 *    first item that has a bound file (or `null` if none do). Callers that
 *    need to validate a SPECIFIC `mediaFileId` against the action should
 *    use {@link doppeActionContainsBoundDownloadFileId} instead, which
 *    searches all items.
 * 4. Any `DoppeActionCTAType.DownloadFile` CTA button nested inside another
 *    action's `ctaButtons` (the legacy CTA-option case) — handled by
 *    `doppeActionCtaButtonsFindDownloadFileMediaFileId`.
 *
 * Returns `null` when the action has no download binding. Used by the
 * "is this action download-capable?" predicates and by the post-checkout
 * thank-you flow to discover paid downloads on a page.
 */
export function doppeActionResolveBoundDownloadFileId(
    action: DoppeDtoAction,
): string | null {
    if (action.actionTypeId === doppeActionTypeDownloadFile.id) {
        const settings = action.settings as DoppeActionTypeDownloadFile.ActionSettings;
        return settings.fileId || null;
    }
    if (action.actionTypeId === doppeActionTypeDownloadFilePreview.id) {
        const settings = action.settings as DoppeActionTypeDownloadFilePreview.ActionSettings;
        return settings.fileSettings?.fileId ?? null;
    }
    if (action.actionTypeId === doppeActionTypeDownloadFilesList.id) {
        const settings = action.settings as DoppeActionTypeDownloadFilesList.ActionSettings;
        const firstBoundItem = settings.listDataItems.find(item => !!item.fileSettings?.fileId);
        return firstBoundItem?.fileSettings?.fileId ?? null;
    }
    return doppeActionCtaButtonsFindDownloadFileMediaFileId(action);
}

/**
 * Tests whether a specific `mediaFileId` is bound to the given action.
 *
 * Used by the server's `assertDownloadFileCtaActionContext` to validate
 * `generateFreeDownloadFileUrl` / `generatePaidDownloadFileUrl` requests
 * — locks each request to a file the action's creator actually configured
 * (preventing callers from minting signed URLs for arbitrary Wix Media
 * files they happen to know).
 *
 * Unlike {@link doppeActionResolveBoundDownloadFileId} (which returns a
 * single representative id and is therefore best-effort for list
 * actions), this checks ALL bindings of an action — for list actions it
 * searches every list item's `fileSettings`.
 */
export function doppeActionContainsBoundDownloadFileId(
    action: DoppeDtoAction,
    mediaFileId: string,
): boolean {
    if (action.actionTypeId === doppeActionTypeDownloadFile.id) {
        const settings = action.settings as DoppeActionTypeDownloadFile.ActionSettings;
        return settings.fileId === mediaFileId;
    }
    if (action.actionTypeId === doppeActionTypeDownloadFilePreview.id) {
        const settings = action.settings as DoppeActionTypeDownloadFilePreview.ActionSettings;
        return settings.fileSettings?.fileId === mediaFileId;
    }
    if (action.actionTypeId === doppeActionTypeDownloadFilesList.id) {
        const settings = action.settings as DoppeActionTypeDownloadFilesList.ActionSettings;
        return iterableAny(settings.listDataItems, item => item.fileSettings?.fileId === mediaFileId);
    }
    return doppeActionCtaButtonsFindDownloadFileMediaFileId(action) === mediaFileId;
}
